<template>
  <BlockContainer class="p-5">
    <ValidationObserver v-slot="{ invalid, dirty }">
      <!-- Error patching -->
      <hl-Modal
        v-if="showErrorModal"
        class="text-center"
        iconClass="budicon-cloud-server text-warning"
        title="Oops! ️️🕵️‍♂️ Noe gikk galt. Vennligst prøv på nytt."
      >
        <p>Vi klarte ikke lagre dine endringer.</p>
        <hl-Loader v-if="loading" variation="warning" />

        <div v-show="!loading">
          <button class="btn btn-warning mx-2" @click="patchRentalObject">Prøv igjen</button>
          <button class="btn btn-outline-secondary mx-2" @click="showErrorModal = false">Avbryt</button>
        </div>
      </hl-Modal>
      <!-- Error patching -->

      <div class="row align-items-center">
        <div class="col">
          <h5>Leieobjektet</h5>
        </div>

        <div class="col edit-buttons text-right">
          <div v-if="editMode">
            <CancelButton class="mr-1" @click.native="editMode = false" />
            <SaveButton
              :loading="loading"
              @click.native="patchRentalObject"
              :disabled="invalid || !dirty"
              class="ml-1"
            />
          </div>

          <EditButton v-else @click.native="setEditModeAndLocalCopy" />
        </div>
      </div>

      <div class="row mt-5">
        <div class="col">
          <strong>Navn / alias</strong>
        </div>

        <div v-if="!loaded || !rentalObject.details" class="col">
          <hl-Mask :width="80"></hl-Mask>
        </div>

        <div v-else class="col">
          <ValidationProvider v-if="editMode" name="NameAlias">
            <hl-Input type="text" size="sm" v-model="localCopy.details.alias" noValidation />
          </ValidationProvider>

          <span v-else>
            <span v-if="rentalObject.details.alias">{{ rentalObject.details.alias }}</span>
          </span>
        </div>
      </div>

      <div class="row mt-4">
        <div class="col">
          <strong>Adresse</strong>
        </div>

        <div v-if="!loaded || !rentalObject.address" class="col">
          <hl-Mask :width="80"></hl-Mask>
        </div>

        <div v-else class="col">
          <ValidationProvider
            v-if="editMode"
            name="Adresse"
            :rules="validationRules.rentalObject.rentalObject.street"
            v-slot="{ valid, errors }"
          >
            <hl-Input type="text" size="sm" v-model="localCopy.address.street" :error="errors[0]" />
          </ValidationProvider>

          <span v-else>
            <span>{{ rentalObject.address.street }}</span>
          </span>
        </div>
      </div>

      <div class="row mt-4">
        <div class="col">
          <strong>Postnummer</strong>
        </div>

        <div v-if="!loaded || !rentalObject.address" class="col">
          <hl-Mask :width="80"></hl-Mask>
        </div>

        <div v-else class="col">
          <ValidationProvider
            v-if="editMode"
            ref="postalZipcode"
            name="Postnummer"
            rules="required|numeric|min:4|max:4"
            v-slot="{ errors }"
          >
            <hl-Input
              type="text"
              size="sm"
              v-model="localCopy.address.postalZipcode"
              @input="fetchPostalArea(arguments[0])"
              :error="errors[0]"
            />
          </ValidationProvider>
          <span v-else>
            <span>{{ rentalObject.address.postalZipcode }}</span>
          </span>
        </div>
      </div>

      <div class="row mt-4">
        <div class="col">
          <strong>Poststed</strong>
        </div>

        <div v-if="!loaded || !rentalObject.address" class="col">
          <hl-Mask :width="80"></hl-Mask>
        </div>

        <div v-else class="col">
          <ValidationProvider
            v-if="editMode"
            ref="postalArea"
            name="Poststed"
            :rules="{
              required: true,
            }"
            v-slot="{ errors }"
          >
            <hl-Input type="text" size="sm" v-model="localCopy.address.postalArea" disabled :error="errors[0]" />
          </ValidationProvider>

          <span v-else>{{ rentalObject.address.postalArea }}</span>
        </div>
      </div>

      <div class="row mt-4">
        <div class="col">
          <strong>Leilighetsnummer</strong>
        </div>

        <div v-if="!loaded || !rentalObject.details" class="col">
          <hl-Mask :width="80"></hl-Mask>
        </div>

        <div v-else class="col">
          <ValidationProvider v-if="editMode" name="ApartmentNumber">
            <hl-Input type="text" size="sm" v-model="localCopy.details.apartmentNumber" noValidation />
          </ValidationProvider>

          <span v-else>
            <span>{{ rentalObject.details.apartmentNumber }}</span>
          </span>
        </div>
      </div>

      <div class="row mt-4">
        <div class="col">
          <strong>Romnummer</strong>
        </div>

        <div v-if="!loaded || !rentalObject.details" class="col">
          <hl-Mask :width="80"></hl-Mask>
        </div>

        <div v-else class="col">
          <ValidationProvider v-if="editMode" name="roomNumber">
            <hl-Input type="text" size="sm" v-model="localCopy.details.roomNumber" noValidation />
          </ValidationProvider>

          <span v-else>
            <span>{{ rentalObject.details.roomNumber === '0' ? '' : rentalObject.details.roomNumber }}</span>
          </span>
        </div>
      </div>

      <div v-if="loaded && rentalObject.details.professional_landlord" class="row mt-4">
        <div class="col">
          <strong>ByggID</strong>
        </div>

        <div v-if="!loaded || !rentalObject.details" class="col">
          <hl-Mask :width="80"></hl-Mask>
        </div>

        <div v-else class="col">
          <ValidationProvider v-if="editMode" name="buildingId">
            <hl-Input type="text" size="sm" v-model="localCopy.details.buildingId" noValidation />
          </ValidationProvider>

          <span v-else>
            <span>{{ rentalObject.details.buildingId }}</span>
          </span>
        </div>
      </div>

      <div class="mt-4">
        <h6>Notat</h6>
        <div v-if="!loaded || !rentalObject.details">
          <hl-Mask :height="40" width="100%"></hl-Mask>
        </div>

        <div v-else>
          <ValidationProvider v-if="editMode" name="comments">
            <hl-Textarea v-model="localCopy.details.comment" :rows="5"></hl-Textarea>
          </ValidationProvider>
          <div v-else>
            <pre class="mt-4 pre-scrollable pre-responsive">{{
              rentalObject.details.comment ? rentalObject.details.comment : ''
            }}</pre>
            <div v-if="rentalObject.details.comment.length > 1024" class="scroll-down text-center">
              <span>Scroll down</span>
              <i class="budicon-arrow-bottom ml-3"></i>
            </div>
          </div>
        </div>
      </div>
    </ValidationObserver>
  </BlockContainer>
</template>

<script>
import axios from 'axios';
import BlockContainer from '@/components/BlockContainer.vue';
import CancelButton from '@/components/UI/buttons/CancelButton';
import EditButton from '@/components/UI/buttons/EditButton';
import SaveButton from '@/components/UI/buttons/SaveButton';
import addValidationRules from '@/mixins/addValidationRules.js';
import { mapGetters } from 'vuex';
import Modal from '@modules/husleie-framework/src/components/modals/Modal.vue';
import Input from '@modules/husleie-framework/src/components/forms/inputs/input/Input.vue';
import Mask from '@modules/husleie-framework/src/components/loaders/Mask.vue';
import Loader from '@modules/husleie-framework/src/components/loaders/Loader.vue';
import Textarea from '@modules/husleie-framework/src/components/forms/Textarea.vue';
import postalCodeLookup from '@/lib/postalCodeLookup';

export default {
  components: {
    BlockContainer,
    CancelButton,
    EditButton,
    SaveButton,
    'hl-Modal': Modal,
    'hl-Mask': Mask,
    'hl-Input': Input,
    'hl-Loader': Loader,
    'hl-Textarea': Textarea,
  },

  mixins: [addValidationRules],

  data() {
    return {
      editMode: false,
      isSaveLocked: false,
      showErrorModal: false,
      loading: false,
      localCopy: null,
      postalAreaCancelSource: undefined,
    };
  },

  props: {
    rentalObjectId: [Number, String],
  },

  computed: {
    ...mapGetters(['getRentalObject', 'isExtendedRentalObjectLoaded']),

    rentalObject() {
      return this.getRentalObject(this.rentalObjectId);
    },

    loaded() {
      return this.isExtendedRentalObjectLoaded;
    },

    patchBody() {
      if (this.localCopy == null) return;
      return {
        address: {
          postalArea: this.localCopy.address.postalArea,
          postalZipcode: this.localCopy.address.postalZipcode,
          street: this.localCopy.address.street,
        },

        details: {
          alias: this.localCopy.details.alias,
          apartmentNumber: this.localCopy.details.apartmentNumber,
          roomNumber: this.localCopy.details.roomNumber,
          buildingId: this.localCopy.details.buildingId,
          comment: this.localCopy.details.comment,
        },
      };
    },
  },

  methods: {
    setEditModeAndLocalCopy() {
      this.editMode = true;
      this.$set(this, 'localCopy', JSON.parse(JSON.stringify(this.rentalObject)));
    },

    patchRentalObject() {
      this.loading = true;
      this.$store.dispatch('patchRentalObject', {
        rentalObjectId: this.rentalObjectId,
        body: this.patchBody,
        success: () => {
          this.editMode = false;
          this.$toast('Lagret', 'success');
        },
        error: () => {
          this.showErrorModal = true;
          this.$toast('Kunne ikke lagre', 'error');
        },
      });
      this.loading = false;
    },

    async fetchPostalArea(postalCode) {
      if (this.postalAreaCancelSource) {
        this.postalAreaCancelSource.cancel();
        this.postalAreaCancelSource = undefined;
      }

      this.localCopy.address.postalArea = '';

      if (postalCode.length !== 4) {
        return;
      }

      try {
        this.postalAreaCancelSource = axios.CancelToken.source();
        const res = await postalCodeLookup(postalCode, this.postalAreaCancelSource.token);

        if (res.error) {
          this.$refs.postalZipcode.setErrors(['Postnummer ikke funnet']);
        } else {
          this.localCopy.address.postalArea = res.poststed;
        }
      } catch (error) {
        if (axios.isCancel(error)) return;
        this.$toast('Kunne ikke hente poststed. Sjekk at postnummeret har gyldig format.', 'error');
      }
    },
  },
};
</script>

<style scoped lang="scss">
.row {
  min-height: 31px;
  align-items: center;
}
</style>
